import React, { useState } from 'react';
import { BackButton, Button, Input, ErrorMessage, SuccessMessage } from '../ui';
import DiceBackground from '../DiceBackground';
import { MIN_PASSWORD_LENGTH } from '../../constants';
import { useTranslation } from '../../i18n';

interface ResetPasswordProps {
  onUpdatePassword: (newPassword: string) => Promise<void>;
  onBack: () => void;
}

export const ResetPassword: React.FC<ResetPasswordProps> = ({
  onUpdatePassword,
  onBack,
}) => {
  const t = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError(t.enterNewPassword);
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t.passwordMinLength(MIN_PASSWORD_LENGTH));
      return;
    }
    if (password !== confirmPassword) {
      setError(t.passwordsNoMatch);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await onUpdatePassword(password);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.failedUpdatePassword);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center text-white p-4 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={12} />

      <BackButton onClick={onBack} />

      <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-8 text-theme drop-shadow-lg uppercase text-center relative z-10">
        {t.newPassword}
      </h1>

      <div className="w-full max-w-xs lg:max-w-md relative z-10">
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {success ? (
          <div className="flex flex-col gap-4">
            <SuccessMessage>{t.passwordUpdated}</SuccessMessage>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <section className="flex flex-col gap-2">
              <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
                {t.newPasswordLabel}
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading}
                className="!normal-case !tracking-normal"
              />
            </section>

            <section className="flex flex-col gap-2">
              <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
                {t.confirmPassword}
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading}
                className="!normal-case !tracking-normal"
              />
              <span className="text-[8px] lg:text-[9px] text-gray-400 text-center">
                {t.minimumChars(MIN_PASSWORD_LENGTH)}
              </span>
            </section>

            <Button type="submit" variant="success" size="lg" disabled={isLoading}>
              {isLoading ? t.updating : t.updatePassword}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
